import React, { useEffect, useRef, useMemo } from 'react';
import MathRenderer from '../common/MathRenderer.v3';
import { useStore } from '../../stores';
import { buildStaticUrl } from '../../config/env';
import { User } from 'lucide-react';

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  topicId?: string;
  model?: 'gemini' | 'deepseek';
  tokenCount?: number;
  responseTime?: number;
  // 🆕 多模态支持
  messageType?: 'text' | 'image';
  imageUrl?: string;
  imageSize?: number;
  attachments?: {
    images?: Array<{
      id: string;
      url: string;
      filename: string;
      size: number;
      mimeType: string;
    }>;
  };
}

interface ChatMessageAreaProps {
  messages: Message[];
  isTyping: boolean;
  isUploading: boolean;
  loadingHistory: boolean;
  solutionButtonShown: Set<string>;
  connected: boolean;
  currentTopic: any;
  historySessionId: string | null;
  onRequestSolution: () => void;
  onRemoveSolutionButton: (messageId: string) => void;
}

/**
 * 聊天消息显示区域组件
 * 独立的消息列表，避免输入时重新渲染
 */
export const ChatMessageArea: React.FC<ChatMessageAreaProps> = React.memo(({
  messages,
  isTyping,
  isUploading,
  loadingHistory,
  solutionButtonShown,
  connected,
  currentTopic,
  historySessionId,
  onRequestSolution,
  onRemoveSolutionButton
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useStore();

  // 获取AI模型头像URL - 从用户数据库读取
  const getAIAvatar = (model?: string) => {
    // 优先使用用户自定义的 AI 头像（从数据库读取）
    if (model === 'gemini') {
      if (user?.ai_avatar_gemini) {
        return buildStaticUrl(user.ai_avatar_gemini);
      }
      // 回退到默认头像
      return buildStaticUrl('/uploads/xid_teacher1.jpg');
    } else if (model === 'deepseek') {
      if (user?.ai_avatar_deepseek) {
        return buildStaticUrl(user.ai_avatar_deepseek);
      }
      // 回退到默认头像
      return buildStaticUrl('/uploads/xid_teacher2.jpg');
    }
    
    // 未指定模型或不匹配时返回logo
    return buildStaticUrl('/uploads/ai.jpg');
  };

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 使用useMemo优化消息渲染，只有相关依赖变化时才重新渲染
  const renderedMessages = useMemo(() => {
    return messages.map((message) => (
      <div key={message.id}>
        <div
          className={`flex items-start gap-2 ${
            message.role === 'user' 
              ? 'justify-end' 
              : message.role === 'system'
                ? 'justify-center'
                : 'justify-start'
          }`}
        >
          {/* AI消息头像 - 左侧（系统消息不显示头像） */}
          {message.role === 'assistant' && (
            <div className="flex-shrink-0 mt-1">
              {currentTopic ? (
                <img
                  src={getAIAvatar(message.model)}
                  alt="AI"
                  className="w-8 h-8 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                  onError={(e) => {
                    // 图片加载失败时显示默认图标
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove('hidden');
                  }}
                />
              ) : (
                <img
                  src={buildStaticUrl('/uploads/ai.jpg')}
                  alt="AI"
                  className="w-8 h-8 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                />
              )}
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-600 hidden">
                <User className="w-5 h-5 text-white" />
              </div>
            </div>
          )}

          {/* 消息气泡 */}
          <div
            className={`max-w-xs sm:max-w-md lg:max-w-lg xl:max-w-xl rounded-2xl px-4 py-3 text-sm ${message.role === 'user'
              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
              : message.role === 'system'
                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border border-orange-200 dark:border-orange-800'
                : 'bg-white border border-gray-200 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-white'
              }`}
          >
            {/* 🆕 图片消息支持 */}
            {message.messageType === 'image' && message.imageUrl && (
              <div className="mb-3">
                <img
                  src={buildStaticUrl(message.imageUrl)}
                  alt="用户上传的图片"
                  className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ maxHeight: '200px' }}
                  onClick={() => {
                    // 点击图片预览
                    if (message.imageUrl) {
                      window.open(buildStaticUrl(message.imageUrl), '_blank');
                    }
                  }}
                  onError={(e) => {
                    // 图片加载失败时显示占位符
                    e.currentTarget.style.display = 'none';
                    const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                    if (placeholder) {
                      placeholder.style.display = 'block';
                    }
                  }}
                />
                <div 
                  className="hidden bg-gray-100 dark:bg-gray-700 rounded-lg p-4 text-center text-gray-500 dark:text-gray-400"
                  style={{ display: 'none' }}
                >
                  📷 图片加载失败
                </div>
              </div>
            )}
            
            {/* 🆕 附件图片支持（多图片） */}
            {message.attachments?.images && message.attachments.images.length > 0 && (
              <div className="mb-3 space-y-2">
                {message.attachments.images.map((image, index) => (
                  <div key={image.id || index}>
                    <img
                      src={buildStaticUrl(image.url)}
                      alt={image.filename || `图片 ${index + 1}`}
                      className="max-w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      style={{ maxHeight: '200px' }}
                      onClick={() => {
                        window.open(buildStaticUrl(image.url), '_blank');
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                        if (placeholder) {
                          placeholder.style.display = 'block';
                        }
                      }}
                    />
                    <div 
                      className="hidden bg-gray-100 dark:bg-gray-700 rounded-lg p-2 text-center text-xs text-gray-500 dark:text-gray-400"
                      style={{ display: 'none' }}
                    >
                      📷 {image.filename || '图片'} 加载失败
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 🆕 只有当content不为空时才显示文本内容 */}
            {message.content && message.content.trim() && (
              <MathRenderer content={message.content} className="text-sm whitespace-pre-wrap" />
            )}
            <div className={`text-xs mt-2 ${message.role === 'user'
              ? 'text-blue-100'
              : 'text-gray-500 dark:text-gray-400'
              }`}>
              {message.timestamp.toLocaleTimeString()}
            </div>
          </div>

          {/* 用户消息头像 - 右侧 */}
          {message.role === 'user' && (
            <div className="flex-shrink-0 mt-1">
              {user?.avatar ? (
                <img
                  src={buildStaticUrl(user.avatar, true)}
                  alt="用户头像"
                  className="w-8 h-8 rounded-full object-cover border-2 border-blue-300"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                  {user?.nickname?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* 在完成消息后添加解题过程按钮 */}
        {message.role === 'system' &&
          message.id.startsWith('completion_') &&
          solutionButtonShown.has(message.id) && (
            <div className="flex justify-center mt-3">
              <button
                onClick={() => {
                  onRequestSolution();
                  // 点击后移除按钮，避免重复点击
                  onRemoveSolutionButton(message.id);
                }}
                disabled={isTyping || !connected}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 
                       text-white rounded-lg transition-colors disabled:cursor-not-allowed
                       text-sm font-medium"
              >
                📝 请对学习过程进行总结
              </button>
            </div>
          )}
      </div>
    ));
  }, [messages, solutionButtonShown, isTyping, connected, onRequestSolution, onRemoveSolutionButton, user]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* 标题栏 */}
      <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
            💬 对话窗口
            {/* 移除模型显示 */}
            {historySessionId && (
              <span className="ml-3 text-sm font-normal text-blue-600 dark:text-blue-400">
                📚 历史会话
              </span>
            )}
          </h1>

          {currentTopic && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {currentTopic.category === 'math' && '📐 数学'}
              {currentTopic.category === 'physics' && '⚛️ 物理'}
              {currentTopic.category === 'chemistry' && '🧪 化学'}
              {currentTopic.category === 'others' && '📚 其他'}
            </div>
          )}
        </div>

        {/* 连接状态 */}
        <div className={`mt-2 px-3 py-1 rounded-full text-sm font-medium inline-block ${connected
          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}>
          {connected ? '🟢 已连接' : '🔴 已断开'}
        </div>
      </div>

      {/* 消息列表 - 可滚动区域 */}
      <div className="flex-1 overflow-y-auto p-3 lg:p-5 bg-gray-50 dark:bg-gray-900 mobile-scroll">
        <div className="space-y-4">
          {renderedMessages}

          {(isTyping || isUploading || loadingHistory) && (
            <div className="flex items-start gap-2 justify-start">
              {/* AI头像 */}
              <div className="flex-shrink-0 mt-1">
                {currentTopic ? (
                  <img
                    src={getAIAvatar(currentTopic.modelType)}
                    alt="AI"
                    className="w-8 h-8 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : (
                  <img
                    src={buildStaticUrl('/uploads/ai.jpg')}
                    alt="AI"
                    className="w-8 h-8 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                  />
                )}
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-600 hidden">
                  <User className="w-5 h-5 text-white" />
                </div>
              </div>
              
              {/* 加载动画 */}
              <div className="bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-700 rounded-2xl px-4 py-3">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {loadingHistory ? '加载历史会话中...' : isUploading ? '分析中...' : 'AI思考中...'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 滚动锚点 */}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
});

ChatMessageArea.displayName = 'ChatMessageArea';

export default ChatMessageArea;